import type Assessment from '@app/src/types/assessment'
import type { BenchmarkGroupType } from '@app/src/types/benchmark'
import type { SustainabilityTopic } from '@app/src/types/topicalRisk'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { AssessmentLinkResolver } from '../assessmentLinks'
import { peerGroupOptionMessageId } from '../benchmark/peerGroups'
import type { BaselineBenchmark } from '../benchmark/types'
import type { ProfileAreaSection } from '../profileLogic'
import { buildBaselineAreaRows, hasTopicScores } from '../profileLogic'
import BaselinePerformanceSection from './BaselinePerformanceSection'
import TopicPerformanceSection from './TopicPerformanceSection'

interface ProfilePerformanceSectionsProps {
  topics: SustainabilityTopic[]
  sections: ProfileAreaSection[]
  baseline?: Assessment
  peerBaseline?: BaselineBenchmark
  peerGroup: BenchmarkGroupType
  isLoading: boolean
  resolveLink: AssessmentLinkResolver
  overallRisk?: React.ComponentProps<typeof TopicPerformanceSection>['overallRisk']
  emptyState?: React.ReactNode
  screeningTemplateIds?: Set<number>
}

const ProfilePerformanceSections: React.FC<ProfilePerformanceSectionsProps> = ({
  topics,
  sections,
  baseline,
  peerBaseline,
  peerGroup,
  isLoading,
  resolveLink,
  overallRisk,
  emptyState,
  screeningTemplateIds,
}) => {
  const { formatMessage } = useIntl()
  const baselineAreaRows = useMemo(
    () => (baseline ? buildBaselineAreaRows(topics, baseline, peerBaseline) : []),
    [topics, baseline, peerBaseline],
  )

  const baselineSection = baseline && (
    <BaselinePerformanceSection
      key="baseline"
      baseline={baseline}
      peerBaseline={peerBaseline}
      peerGroup={peerGroup}
      areaRows={baselineAreaRows}
      link={resolveLink(baseline.assessmentTemplateId, baseline.periodName)}
    />
  )
  const topicSection = (
    <TopicPerformanceSection
      key="topics"
      sections={sections}
      filterLabel={formatMessage({ id: peerGroupOptionMessageId(peerGroup) })}
      isLoading={isLoading}
      overallRisk={overallRisk}
      emptyState={emptyState}
      screeningTemplateIds={screeningTemplateIds}
      resolveLink={resolveLink}
    />
  )

  const isBaselineFirst = !isLoading && !hasTopicScores(sections)
  return <>{isBaselineFirst ? [baselineSection, topicSection] : [topicSection, baselineSection]}</>
}

export default ProfilePerformanceSections
